// 🔴 N°601 delta 1 — « ENVOYER UNE VISITE » : LE PONT DOSSIER → VISITE.
//
// ⚠️ MESURÉ : le moteur de visite existait (POST /api/visite/saisies), mais
// ⛔ AUCUN écran de dossier ⛔ ne le déclenchait — et le bloc DAL, lui, est
// enfermé derrière `isTEF`, donc INVISIBLE pour tout autre tenant. C'est la
// règle en creux : un comportement métier décidé par le SLUG.
// 🔑 ICI, la porte est une CAPABILITY DÉCLARÉE (`operation.visites`, celle du
// Visit Engine) : Mario l'a, TEF garde son bloc DAL INTACT, et un tenant qui
// activera la capability demain verra le bouton sans qu'on touche au code.
//
// CE QUE FAIT LE BOUTON, ET RIEN D'AUTRE :
//   1. ouvre (ou REPREND — la route est idempotente) la visite du dossier ;
//   2. demande son LIEN DE MISSION ;
//   3. affiche le lien À COPIER — le secret est APRÈS le « # », donc il ⛔ ne
//      part JAMAIS au serveur (⛔ ni journal nginx, ⛔ ni Referer).
// ⚠️ LE CODE N'EST RENDU QU'UNE FOIS : on le DIT à l'écran. Il n'est ⛔ PAS
// relisible ensuite — on en crée un nouveau.
import { useEffect, useState } from "react";
import { Copy, Link2, Loader2, RefreshCw } from "lucide-react";
import { usePermissions } from "../../../contexts/PermissionContext";
// 🔑 LA MÊME date française que partout ailleurs — ⛔ pas un second format.
import { formatDateFR } from "../../../utils/dateFormat";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function appel(url, options = {}) {
  let jeton = null;
  try { jeton = localStorage.getItem("session_token"); } catch (e) { /* indispo */ }
  const entetes = { "Content-Type": "application/json" };
  if (jeton) entetes.Authorization = `Bearer ${jeton}`;
  const r = await fetch(`${API}${url}`, { credentials: "include",
                                          headers: entetes, ...options });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const d = j?.detail;
    throw new Error(typeof d === "string" ? d : `Échec (HTTP ${r.status})`);
  }
  return j;
}

export function EnvoyerVisiteBlock({ dossierId, dossier }) {
  const { isFeatureEnabled } = usePermissions();
  const [profils, setProfils] = useState([]);
  const [code, setCode] = useState("");
  const [canal, setCanal] = useState("sms");
  // 🔴 N°628 — CE QUE L'ÉCRAN TEF FAIT, MESURÉ : il MONTRE le visiteur, PRÉ-REMPLIT
  // ses coordonnées et laisse MODIFIER avant d'envoyer. Un envoi dont on ⛔ ne sait
  // PAS où il va ⛔ n'est pas livrable.
  const [visite, setVisite] = useState(null);      // { id } de la visite ouverte
  // 🔴 N°696-A — le brouillon EXISTANT rendu par le serveur, tant que
  // l'utilisateur ⛔ n'a PAS tranché. ⛔ Rien n'est envoyé dans cet état.
  const [contact, setContact] = useState(null);    // { visiteur, email, telephone }
  const [destinataire, setDestinataire] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [lien, setLien] = useState(null);
  const [copie, setCopie] = useState(false);

  // ⛔ UNKNOWN ≠ ENABLED (DEC-079) : `isFeatureEnabled` rend `false` tant que le
  // contexte n'est pas chargé. Le bloc ⛔ n'apparaît donc JAMAIS « au cas où ».
  const autorise = isFeatureEnabled("operation.visites");

  useEffect(() => {
    if (!autorise) return;
    (async () => {
      try {
        const p = await appel("/api/visite/profils");
        const liste = p?.profils || [];
        setProfils(liste);
        if (liste.length === 1) setCode(liste[0].code);
      } catch (e) { setErreur(e.message); }
    })();
  }, [autorise]);

  if (!autorise) return null;

  // 🔴 N°624 — LE GESTE NORMAL EST L'ENVOI, ⛔ PAS UN COPIER-COLLER.
  // ⚠️ Ma première version créait un lien et laissait l'utilisateur le copier :
  // ce ⛔ n'était PAS le parcours produit. Le serveur envoie au VISITEUR DU
  // DOSSIER, par les canaux déjà en place, et journalise — comme le DAL.
  // Le lien reste affiché ENSUITE, en SECOURS.
  // 1er temps — PRÉPARER : ouvrir (ou reprendre) la visite et AFFICHER à qui
  // le lien partira. ⛔ Rien n'est envoyé ici, ⛔ aucune mission n'est créée.
  // (Le choix « Reprendre / Nouvelle » du N°696-A est retiré par le N°837.)
  // 🔴 N°837 (GO JJ) — ENVOI DIRECT. Le serveur (`ouvrir`) rend la visite en
  // cours s'il en existe une (`reprise_existante`), sinon il en crée une : le
  // lien mène à CELLE-LÀ, et l'écran le DIT en une ligne. ⛔ Plus de choix
  // « Reprendre / Nouvelle », ⛔ plus d'abandon.
  // 🔑 L'acquis du N°527 tient : rien n'est abandonné en silence — repartir de
  // zéro, c'est JJ qui SUPPRIME la visite, explicitement (N°829).
  const preparer = async () => {
    setErreur(null); setOccupe(true); setCopie(false); setLien(null);
    try {
      const v = await appel("/api/visite/saisies", {
        method: "POST",
        body: JSON.stringify({ dossier_id: dossierId, profil_code: code }),
      });
      setVisite(v);
      await preparerContact(v);
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  const preparerContact = async (v) => {
    try {
      const c = await appel(`/api/visite/saisies/${v.id}/mission/destinataire`);
      setContact(c);
      setDestinataire((canal === "email" ? c.email : c.telephone) || "");
    } catch (e) { setErreur(e.message); }
  };

  // Changer de canal re-propose la coordonnée CORRESPONDANTE (comme chez TEF).
  // 🔴 N°829 — SUPPRIMER la visite en cours, puis préparer une visite neuve.
  // La garde est côté SERVEUR (terminée ou avec devis ⇒ refus motivé).
  const supprimerEnCours = async () => {
    if (!visite) return;
    if (!window.confirm(
      `Supprimer la visite du ${formatDateFR(visite.created_at) || ""} ?\n\n`
      + "Ses liens envoyés cesseront de fonctionner. Les photos restent dans le dossier. "
      + "Cette action ne s'annule pas.")) return;
    setErreur(null); setOccupe(true);
    try {
      await appel(`/api/visite/saisies/${encodeURIComponent(visite.id)}`,
                  { method: "DELETE" });
      setContact(null); setVisite(null);
      await preparer();
    } catch (e) { setErreur(e.message); setOccupe(false); }
  };

  const changerCanal = (v) => {
    setCanal(v);
    if (contact) setDestinataire((v === "email" ? contact.email : contact.telephone) || "");
  };

  // 2e temps — ENVOYER à ce que l'utilisateur A SOUS LES YEUX.
  const envoyer = async () => {
    // 🔴 N°825 — ⛔ JAMAIS un envoi sans avoir MONTRÉ à qui et à quel numéro.
    // Mesuré en PROD le 28/09 11:30 : « Envoyer » cliqué directement ⇒ SMS parti
    // sans que JJ ait vu le destinataire. Premier clic = on AFFICHE le visiteur
    // et son numéro (modifiable) ; le clic suivant envoie.
    if (!contact) { await preparer(); return; }
    setErreur(null); setOccupe(true); setCopie(false);
    try {
      const v = visite || await appel("/api/visite/saisies", {
        method: "POST",
        body: JSON.stringify({ dossier_id: dossierId, profil_code: code }),
      });
      // 🔴 N°696-A — MÊME GARDE SUR LE CHEMIN DIRECT : « Envoyer » sans avoir
      // préparé tombait sur le brouillon existant et l'envoyait EN SILENCE.
      // ⛔ Une garde sur UN SEUL des deux chemins ⛔ n'est PAS une garde.
      const r = await appel(`/api/visite/saisies/${v.id}/mission/envoyer`, {
        method: "POST",
        body: JSON.stringify({ canal, destinataire: destinataire.trim() || null }),
      });
      setLien({ url: r.url, expire_le: r.expire_le, envoye: r.envoye,
                visiteur: r.visiteur, destinataire: r.destinataire,
                canal: r.canal, erreurEnvoi: r.erreur,
                // 🔴 N°702-3 — ce que le serveur SAIT vraiment de l'envoi. `simule` dit
                // « ⛔ rien n'est parti » (preprod), `statutFournisseur` dit l'état À LA CRÉATION
                // (`queued`/`accepted`) — ⛔ jamais « remis ».
                simule: r.simule, statutFournisseur: r.statut_fournisseur,
                reprise: !!v.reprise_existante });
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(lien.url);
      setCopie(true);
    } catch (e) {
      setErreur("Copie impossible sur cet appareil — sélectionnez le lien à la main.");
    }
  };

  return (
    <div className="rounded-lg p-4" data-testid="envoyer-visite-bloc"
         style={{ background: "var(--oh-bg-elevated)",
                  border: "0.5px solid var(--oh-border)" }}>
      <div className="flex items-center gap-2 mb-2" style={{ fontWeight: 500 }}>
        <Link2 className="w-4 h-4" /> Envoyer une visite
      </div>
      <div className="text-xs mb-3" style={{ color: "var(--oh-text-secondary)",
                                             lineHeight: 1.6 }}>
        Le lien part au visiteur du dossier. Il ouvre sa visite directement,
        sans mot de passe, et peut être rouvert autant de fois que nécessaire
        pendant 72 heures.
      </div>
      <div className="flex gap-2 mb-3">
        {[["sms", "SMS"], ["email", "E-mail"], ["whatsapp", "WhatsApp"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => changerCanal(v)}
                  data-testid={`envoyer-visite-canal-${v}`}
                  className="flex-1 rounded-lg text-xs"
                  style={{ height: 32,
                           background: canal === v ? "var(--oh-accent-bg)" : "transparent",
                           border: "0.5px solid "
                                   + (canal === v ? "var(--oh-accent)" : "var(--oh-border)"),
                           color: canal === v ? "var(--oh-accent-text)"
                                              : "var(--oh-text-secondary)" }}>
            {l}
          </button>
        ))}
      </div>
      {profils.length > 1 && (
        <select value={code} onChange={(e) => setCode(e.target.value)}
                data-testid="envoyer-visite-profil"
                className="w-full mb-3 rounded-lg px-3"
                style={{ height: 36, background: "var(--oh-bg-surface)",
                         border: "0.5px solid var(--oh-border)",
                         color: "var(--oh-text)" }}>
          <option value="">Type de visite…</option>
          {profils.map((p) => (
            <option key={p.code} value={p.code}>{p.libelle || p.code}</option>
          ))}
        </select>
      )}
      {/* 🔴 LE DESTINATAIRE EST VU, ET MODIFIABLE — l'écran TEF fait exactement
          ça : contact pré-rempli depuis la fiche, champ libre à côté. */}
      {contact && (
        <div className="mb-3" data-testid="envoyer-visite-contact">
          <div className="text-xs mb-1" style={{ color: "var(--oh-text-secondary)" }}>
            Visiteur du dossier :{" "}
            <span style={{ color: "var(--oh-text)" }}>
              {contact.visiteur || "aucun visiteur assigné"}
            </span>
            {contact.visiteur && !contact.fiche_trouvee && " — aucune fiche trouvée"}
          </div>
          <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-secondary)" }}>
            {canal === "email" ? "Adresse e-mail" : "Numéro de téléphone"}
          </label>
          <input value={destinataire} onChange={(e) => setDestinataire(e.target.value)}
                 data-testid="envoyer-visite-destinataire"
                 placeholder={canal === "email" ? "visiteur@exemple.fr" : "06 12 34 56 78"}
                 className="w-full rounded-lg px-3 text-sm"
                 style={{ height: 36, background: "var(--oh-bg-surface)",
                          border: "0.5px solid var(--oh-border)",
                          color: "var(--oh-text)" }} />
        </div>
      )}
      {/* 🔴 N°837 — une ligne d'info, ⛔ plus de question : le lien ouvre la
          visite en cours. Pour repartir de zéro : la SUPPRIMER (N°829). */}
      {visite?.reprise_existante && (
        <div className="mb-3 rounded-lg p-3 text-xs" data-testid="envoyer-visite-en-cours"
             style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)",
                      border: "0.5px solid var(--oh-info-border)", lineHeight: 1.6 }}>
          Le lien ouvre la visite commencée le {formatDateFR(visite.created_at)}
          {visite.updated_at
            ? ` (dernière modification le ${formatDateFR(visite.updated_at)})`
            : ""}.
          <button type="button" data-testid="envoyer-visite-supprimer"
                  onClick={supprimerEnCours} disabled={occupe}
                  className="flex items-center justify-center gap-2 w-full rounded-lg text-sm mt-2 disabled:opacity-60"
                  style={{ height: 36, background: "transparent",
                           border: "0.5px solid var(--oh-stat-red-border)",
                           color: "var(--oh-stat-red-text)" }}>
            Supprimer cette visite pour repartir de zéro
          </button>
        </div>
      )}
      {!contact && (
        <button type="button" onClick={() => preparer()} disabled={occupe || !code}
                data-testid="envoyer-visite-preparer"
                className="flex items-center justify-center gap-2 w-full rounded-lg text-sm disabled:opacity-60 mb-2"
                style={{ height: 36, background: "transparent",
                         border: "0.5px solid var(--oh-accent)",
                         color: "var(--oh-accent-text)" }}>
          {occupe ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Préparer l'envoi
        </button>
      )}
      <button type="button" onClick={envoyer}
              disabled={occupe || !code || (!!contact && !destinataire.trim())}
              data-testid="envoyer-visite-btn"
              className="flex items-center justify-center gap-2 w-full rounded-lg text-sm disabled:opacity-60"
              style={{ height: 36, background: "var(--oh-accent-solid)",
                       color: "var(--oh-accent-contrast)" }}>
        {occupe ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Link2 className="w-4 h-4" />}
        {lien ? "Renvoyer un nouveau lien" : "Envoyer la visite au visiteur"}
      </button>
      {erreur && (
        <div className="text-xs mt-3 rounded-lg p-2" data-testid="envoyer-visite-erreur"
             style={{ background: "var(--oh-stat-red-bg)",
                      color: "var(--oh-stat-red-text)", whiteSpace: "pre-line" }}>
          {erreur}
        </div>
      )}
      {lien && (
        <div className="mt-3" data-testid="envoyer-visite-lien">
          {lien.reprise && (
            <div className="text-xs mb-2" style={{ color: "var(--oh-stat-amber-text)" }}>
              Une visite était déjà ouverte sur ce dossier : le lien mène à
              celle-ci.
            </div>
          )}
          {/* 🔴 N°823 — À QUI et À QUEL NUMÉRO, dans TOUS les cas (envoyé, échec,
              simulé) : avant, seul le cas « transmis » les nommait. */}
          <div className="text-xs mb-2" data-testid="envoyer-visite-recap"
               style={{ color: "var(--oh-text)" }}>
            Envoi à {lien.visiteur || contact?.visiteur || "visiteur non renseigné"}
            {" — "}{lien.destinataire || destinataire || "destinataire non renseigné"}
            {lien.canal ? ` (${lien.canal})` : ""}
          </div>
          {/* 🔴 CE QUI S'EST PASSÉ, DIT EN PREMIER : envoyé, ou non. */}
          <div className="text-xs mb-2 rounded-lg p-2" data-testid="envoyer-visite-etat"
               style={{ background: lien.envoye ? "var(--oh-stat-green-bg)"
                                                : "var(--oh-stat-amber-bg)",
                        color: lien.envoye ? "var(--oh-stat-green-text)"
                                           : "var(--oh-stat-amber-text)",
                        lineHeight: 1.6 }}>
            {/* 🔴🔴 N°702-3 — ⛔ PLUS JAMAIS UN VERT QUI MENT.
                ⚠️ « Lien envoyé » s'affichait sur `success: true`, et `success: true` ⛔ ne veut
                PAS dire « reçu ». Deux cas le prouvent, tous deux MESURÉS :
                  · en PREPROD, `egress_blocked` rend `success: true` alors que ⛔ RIEN ⛔ n'est
                    parti (OH-0013) — un test d'envoi y était donc TOUJOURS vert ;
                  · en PROD, Twilio rend `queued`/`accepted` : l'ACCEPTATION de l'opérateur. La
                    remise vient plus tard, et elle peut ⛔ ne jamais arriver sur le téléphone.
                ⇒ On DIT ce qu'on sait, et ⛔ RIEN de plus. */}
            {!lien.envoye
              ? `L'envoi n'a pas abouti${lien.erreurEnvoi ? ` : ${lien.erreurEnvoi}` : ""}. Le lien ci-dessous reste valable — transmettez-le vous-même.`
              : lien.simule
                ? `Aucun ${lien.canal} n'a été envoyé : cet environnement n'émet pas. Utilisez le lien de secours ci-dessous.`
                : `Lien transmis à l'opérateur pour ${lien.visiteur || "le visiteur"} (${lien.destinataire}) par ${lien.canal}`
                  + `${lien.statutFournisseur ? ` — état à l'envoi : ${lien.statutFournisseur}` : ""}.`
                  + ` « Transmis » ne veut pas dire « reçu » : si le visiteur ne l'a pas,`
                  + ` communiquez-lui le lien de secours.`}
          </div>
          <div className="text-xs mb-1" style={{ color: "var(--oh-text-secondary)" }}>
            Secours, si besoin (le lien ne sera plus affiché ensuite) :
          </div>
          <div className="text-xs rounded-lg p-2 break-all"
               data-testid="envoyer-visite-url"
               style={{ background: "var(--oh-bg-surface)",
                        border: "0.5px solid var(--oh-border)" }}>
            {lien.url}
          </div>
          <button type="button" onClick={copier} data-testid="envoyer-visite-copier"
                  className="flex items-center justify-center gap-2 w-full rounded-lg text-sm mt-2"
                  style={{ height: 36, background: "transparent",
                           border: "0.5px solid var(--oh-accent)",
                           color: "var(--oh-accent-text)" }}>
            {copie ? <RefreshCw className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copie ? "Lien copié" : "Copier le lien"}
          </button>
        </div>
      )}
    </div>
  );
}

export default EnvoyerVisiteBlock;
